import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { fetchPersonalizationFonts } from "../api/DesignAPIs";
import { API_BASE_URL_V2 } from "../api/URL_Header";

const PREVIEW_ZOOM_MIN = 25;
const PREVIEW_ZOOM_MAX = 140;
const PREVIEW_ZOOM_STEP = 10;
const PREVIEW_ZOOM_DEFAULT = 50;

export const PERSONALIZATION_FONTS = [
  { id: "cairo-regular", name: "Cairo Regular", cssFamily: "Cairo", weight: 400, supportsArabic: true, supportsEnglish: true },
  { id: "cairo-bold", name: "Cairo Bold", cssFamily: "Cairo", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "cairo-black", name: "Cairo Black", cssFamily: "Cairo", weight: 900, supportsArabic: true, supportsEnglish: true },
  { id: "tajawal-bold", name: "Tajawal Bold", cssFamily: "Tajawal", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "amiri-regular", name: "Amiri", cssFamily: "Amiri", weight: 400, supportsArabic: true, supportsEnglish: true },
  { id: "amiri-bold", name: "Amiri Bold", cssFamily: "Amiri", weight: 700, supportsArabic: true, supportsEnglish: true },
  { id: "battekh-1", name: "Battekh-1", cssFamily: "Battekh", weight: 700, supportsArabic: true, supportsEnglish: false },
  { id: "montserrat-bold", name: "Montserrat Bold", cssFamily: "Montserrat", weight: 700, supportsArabic: false, supportsEnglish: true },
];

export const DEFAULT_LAYER_COLORS = {
  ar: [
    { id: "white", name: "أبيض", hex: "#FFFFFF", enabled: true },
    { id: "black", name: "أسود", hex: "#111111", enabled: true },
    { id: "red", name: "أحمر", hex: "#C41E3A", enabled: true },
    { id: "gold", name: "ذهبي", hex: "#C9A227", enabled: true },
  ],
  en: [
    { id: "white", name: "White", hex: "#FFFFFF", enabled: true },
    { id: "black", name: "Black", hex: "#111111", enabled: true },
    { id: "green", name: "Green", hex: "#15803D", enabled: true },
    { id: "blue", name: "Blue", hex: "#1D4ED8", enabled: true },
  ],
};

const fontFileHref = (font) => {
  const path = font?.fileUrl || font?.file || "";
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_BASE_URL_V2}${path.startsWith("/") ? "" : "/"}${path}`;
};

const loadedAdminFonts = new Map();

function loadAdminFont(font) {
  if (!font || typeof document === "undefined" || !document.fonts) return Promise.resolve();
  const family = font.webFamily || font.cssFamily;
  const href = fontFileHref(font);
  if (!family) return Promise.resolve();
  if (loadedAdminFonts.has(family)) return loadedAdminFonts.get(family);
  if (!href) return Promise.resolve();
  const format = font.format ? ` format("${font.format}")` : "";
  const face = new FontFace(family, `url("${href}")${format}`, { style: "normal", weight: String(font.weight || 400) });
  const promise = face.load().then((loaded) => {
    document.fonts.add(loaded);
    return family;
  }).catch(() => {
    loadedAdminFonts.delete(family);
  });
  loadedAdminFonts.set(family, promise);
  return promise;
}

const LAYER_META = {
  ar: { key: "ar", label: "عربي", script: "arabic", direction: "rtl", sample: "محمد" },
  en: { key: "en", label: "English", script: "latin", direction: "ltr", sample: "Mohamed" },
};

export function createDefaultLayer(key, overrides = {}) {
  const meta = LAYER_META[key] || { key, label: key, script: "latin", direction: "ltr", sample: "Text" };
  const arabic = meta.script === "arabic";
  return {
    key: meta.key,
    label: meta.label,
    enabled: true,
    script: meta.script,
    direction: meta.direction,
    fontId: arabic ? "cairo-bold" : "montserrat-bold",
    fontSizePercent: 8,
    textColor: "#FFFFFF",
    posX: 50,
    posY: arabic ? 78 : 88,
    boxWidthPercent: 50,
    boxHeightPercent: 0,
    alignH: "center",
    alignV: "middle",
    rotation: 0,
    letterSpacing: 0,
    maxLength: 12,
    minLength: 1,
    zIndex: arabic ? 1 : 2,
    colors: (DEFAULT_LAYER_COLORS[meta.key] || DEFAULT_LAYER_COLORS.en).map((color) => ({ ...color })),
    ...overrides,
  };
}

export function defaultLayers() {
  return [createDefaultLayer("ar"), createDefaultLayer("en")];
}

function clamp(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (num < min) return min;
  if (num > max) return max;
  return num;
}

function normalizeHex(value, fallback = "#FFFFFF") {
  let hex = String(value || "").trim();
  if (!hex) return fallback;
  if (!hex.startsWith("#")) hex = `#${hex}`;
  if (hex.length === 4) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex.toUpperCase() : fallback;
}

function normalizeLayer(layer = {}, index = 0) {
  const key = String(layer.key || layer.Key || (index === 0 ? "ar" : "en")).toLowerCase();
  const meta = LAYER_META[key] || LAYER_META.en;
  const arabic = meta.script === "arabic";
  const colors = Array.isArray(layer.colors || layer.Colors)
    ? (layer.colors || layer.Colors).map((color, colorIndex) => ({
        id: String(color.id || color.Id || `c-${colorIndex}`),
        name: color.name || color.Name || (arabic ? "لون" : "Color"),
        hex: normalizeHex(color.hex || color.Hex),
        enabled: color.enabled !== false && color.Enabled !== false,
      }))
    : (DEFAULT_LAYER_COLORS[meta.key] || DEFAULT_LAYER_COLORS.en).map((color) => ({ ...color }));
  const textColor = normalizeHex(layer.textColor || layer.TextColor, colors.find((c) => c.enabled)?.hex || "#FFFFFF");
  if (!colors.some((color) => color.hex === textColor)) {
    colors.unshift({ id: "default", name: arabic ? "افتراضي" : "Default", hex: textColor, enabled: true });
  }
  return {
    key: meta.key,
    label: layer.label || layer.Label || meta.label,
    enabled: layer.enabled !== false && layer.Enabled !== false,
    script: meta.script,
    direction: String(layer.direction || layer.Direction || meta.direction).toLowerCase(),
    fontId: layer.fontId || layer.FontId || (arabic ? "cairo-bold" : "montserrat-bold"),
    fontSizePercent: clamp(layer.fontSizePercent ?? layer.FontSizePercent, 1, 40, 8),
    textColor,
    posX: clamp(layer.posX ?? layer.PosX, 0, 100, 50),
    posY: clamp(layer.posY ?? layer.PosY, 0, 100, arabic ? 78 : 88),
    boxWidthPercent: clamp(layer.boxWidthPercent ?? layer.BoxWidthPercent, 8, 100, 50),
    boxHeightPercent: clamp(layer.boxHeightPercent ?? layer.BoxHeightPercent, 0, 100, 0),
    alignH: String(layer.alignH || layer.AlignH || "center").toLowerCase(),
    alignV: String(layer.alignV || layer.AlignV || "middle").toLowerCase(),
    rotation: clamp(layer.rotation ?? layer.Rotation, -180, 180, 0),
    letterSpacing: clamp(layer.letterSpacing ?? layer.LetterSpacing, -10, 40, 0),
    maxLength: clamp(layer.maxLength ?? layer.MaxLength, 1, 40, 12),
    minLength: clamp(layer.minLength ?? layer.MinLength, 0, 20, 1),
    zIndex: clamp(layer.zIndex ?? layer.ZIndex, 1, 100, arabic ? 1 : 2),
    colors,
  };
}

function synthesizeLegacyLayers(design = {}) {
  const wantsAr = design.requiresArabicName || design.RequiresArabicName || !(design.requiresEnglishName || design.RequiresEnglishName);
  const wantsEn = design.requiresEnglishName || design.RequiresEnglishName || !(design.requiresArabicName || design.RequiresArabicName);
  const shared = {
    fontId: design.personalizationFont || "cairo-bold",
    fontSizePercent: design.personalizationFontSize || 8,
    textColor: design.personalizationTextColor || "#FFFFFF",
    posX: design.personalizationPosX ?? 50,
    posY: design.personalizationPosY ?? 82,
    alignH: design.personalizationAlignH || "center",
    alignV: design.personalizationAlignV || "middle",
    rotation: design.personalizationRotation || 0,
    maxLength: design.maxNameLength || 12,
    minLength: design.minNameLength || 1,
  };
  const layers = [];
  if (wantsAr !== false) {
    layers.push(createDefaultLayer("ar", { ...shared, posY: shared.posY }));
  }
  if (wantsEn !== false) {
    layers.push(createDefaultLayer("en", {
      ...shared,
      fontId: PERSONALIZATION_FONTS.find((font) => font.id === shared.fontId)?.supportsEnglish
        ? shared.fontId
        : "montserrat-bold",
      posY: Math.min(100, Number(shared.posY) + (wantsAr !== false ? 10 : 0)),
    }));
  }
  return layers.length ? layers : defaultLayers();
}

export function syncLegacyFromLayers(layers) {
  const normalized = (layers || []).map(normalizeLayer);
  const ar = normalized.find((layer) => layer.key === "ar");
  const en = normalized.find((layer) => layer.key === "en");
  const primary = normalized.find((layer) => layer.enabled) || ar || normalized[0];
  const enabled = normalized.filter((layer) => layer.enabled);
  return {
    personalizationLayers: normalized,
    personalizationConfig: { version: 2, layers: normalized },
    requiresArabicName: ar?.enabled !== false && Boolean(ar),
    requiresEnglishName: en?.enabled !== false && Boolean(en),
    personalizationFont: primary?.fontId || "cairo-bold",
    personalizationFontWeight: "bold",
    personalizationFontSize: primary?.fontSizePercent ?? 8,
    personalizationTextColor: primary?.textColor || "#FFFFFF",
    personalizationPosX: primary?.posX ?? 50,
    personalizationPosY: primary?.posY ?? 82,
    personalizationAlignH: primary?.alignH || "center",
    personalizationAlignV: primary?.alignV || "middle",
    personalizationRotation: primary?.rotation || 0,
    personalizationTextDirection: primary?.direction || "auto",
    maxNameLength: enabled.length ? Math.max(...enabled.map((layer) => layer.maxLength || 12)) : 12,
    minNameLength: enabled.length ? Math.min(...enabled.map((layer) => layer.minLength ?? 1)) : 1,
  };
}

export function hydratePersonalizationState(design = {}) {
  const raw = design.personalizationLayers || design.PersonalizationLayers
    || design.personalizationConfig?.layers || design.PersonalizationConfig?.layers;
  const layers = Array.isArray(raw) && raw.length
    ? raw.map(normalizeLayer)
    : synthesizeLegacyLayers(design).map(normalizeLayer);
  return {
    allowsPersonalization: Boolean(design.allowsPersonalization || design.AllowsPersonalization),
    ...syncLegacyFromLayers(layers),
  };
}

export const defaultPersonalization = {
  allowsPersonalization: false,
  ...syncLegacyFromLayers(defaultLayers()),
};

const translateX = { left: "0%", center: "-50%", right: "-100%" };
const translateY = { top: "0%", middle: "-50%", bottom: "-100%" };

const LayerFields = ({ layer, onChange, fonts }) => {
  const arabic = layer.script === "arabic";
  const catalog = fonts?.length ? fonts : PERSONALIZATION_FONTS;
  let filtered = catalog.filter((font) => (arabic ? font.supportsArabic : font.supportsEnglish));
  if (layer.fontId && !filtered.some((font) => font.id === layer.fontId)) {
    const current = catalog.find((font) => font.id === layer.fontId);
    if (current) filtered = [current, ...filtered];
  }
  const field = (label, children) => (
    <div className="space-y-1">
      <label className="block text-right text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
  const inputClass = "w-full p-2.5 border-2 border-gray-200 rounded-xl bg-white";

  const moveColor = (index, direction) => {
    const next = [...layer.colors];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange({ colors: next });
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={layer.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
          className="w-5 h-5 text-purple-600 rounded"
        />
        <span className="font-bold text-purple-800">تفعيل طبقة {layer.label}</span>
      </label>

      {layer.enabled && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {field("الخط", (
              <select value={layer.fontId} onChange={(e) => onChange({ fontId: e.target.value })} className={inputClass}>
                {filtered.map((font) => (
                  <option key={font.id} value={font.id}>{font.name}</option>
                ))}
              </select>
            ))}
            {field("حجم الخط (% من عرض الصورة)", (
              <input type="number" min="1" max="40" step="0.1" value={layer.fontSizePercent}
                onChange={(e) => onChange({ fontSizePercent: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("اللون الافتراضي", (
              <div className="flex items-center gap-2">
                <input type="color" value={layer.textColor}
                  onChange={(e) => onChange({ textColor: e.target.value.toUpperCase() })} className="h-11 w-16 border-2 border-gray-200 rounded-xl" />
                <input value={layer.textColor} onChange={(e) => onChange({ textColor: normalizeHex(e.target.value) })} className={inputClass} />
              </div>
            ))}
            {field("المحاذاة الأفقية", (
              <select value={layer.alignH} onChange={(e) => onChange({ alignH: e.target.value })} className={inputClass}>
                <option value="left">يسار</option>
                <option value="center">وسط</option>
                <option value="right">يمين</option>
              </select>
            ))}
            {field("المحاذاة الرأسية", (
              <select value={layer.alignV} onChange={(e) => onChange({ alignV: e.target.value })} className={inputClass}>
                <option value="top">أعلى</option>
                <option value="middle">وسط</option>
                <option value="bottom">أسفل</option>
              </select>
            ))}
            {field("X %", (
              <input type="number" min="0" max="100" step="0.1" value={layer.posX}
                onChange={(e) => onChange({ posX: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("Y %", (
              <input type="number" min="0" max="100" step="0.1" value={layer.posY}
                onChange={(e) => onChange({ posY: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("العرض %", (
              <input type="number" min="8" max="100" step="1" value={layer.boxWidthPercent}
                onChange={(e) => onChange({ boxWidthPercent: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الارتفاع % (0 = تلقائي)", (
              <input type="number" min="0" max="100" step="1" value={layer.boxHeightPercent}
                onChange={(e) => onChange({ boxHeightPercent: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الدوران", (
              <input type="number" min="-180" max="180" step="1" value={layer.rotation}
                onChange={(e) => onChange({ rotation: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("تباعد الحروف", (
              <input type="number" min="-10" max="40" step="0.1" value={layer.letterSpacing}
                onChange={(e) => onChange({ letterSpacing: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("اتجاه النص", (
              <select value={layer.direction} onChange={(e) => onChange({ direction: e.target.value })} className={inputClass}>
                <option value="rtl">RTL</option>
                <option value="ltr">LTR</option>
              </select>
            ))}
            {field("الحد الأقصى", (
              <input type="number" min="1" max="40" value={layer.maxLength}
                onChange={(e) => onChange({ maxLength: Number(e.target.value) })} className={inputClass} />
            ))}
            {field("الحد الأدنى", (
              <input type="number" min="0" max="20" value={layer.minLength}
                onChange={(e) => onChange({ minLength: Number(e.target.value) })} className={inputClass} />
            ))}
          </div>

          <div className="rounded-2xl border border-purple-200 bg-white p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-bold text-purple-800">ألوان العميل المتاحة</p>
              <button
                type="button"
                onClick={() => onChange({
                  colors: [...layer.colors, { id: `c-${Date.now()}`, name: arabic ? "لون جديد" : "New color", hex: "#FFFFFF", enabled: true }],
                })}
                className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm font-semibold"
              >
                إضافة لون
              </button>
            </div>
            <div className="space-y-2">
              {layer.colors.map((color, index) => (
                <div key={color.id || index} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center bg-purple-50/70 p-2 rounded-xl">
                  <input
                    type="color"
                    value={color.hex}
                    onChange={(e) => {
                      const colors = layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, hex: e.target.value.toUpperCase() } : item);
                      onChange({ colors, textColor: index === 0 ? e.target.value.toUpperCase() : layer.textColor });
                    }}
                    className="h-10 w-12 border border-gray-200 rounded-lg"
                  />
                  <input
                    value={color.name}
                    onChange={(e) => onChange({
                      colors: layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, name: e.target.value } : item),
                    })}
                    className="p-2 border border-gray-200 rounded-lg"
                    placeholder={arabic ? "اسم اللون" : "Color name"}
                  />
                  <label className="flex items-center gap-1 text-sm whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={color.enabled}
                      onChange={(e) => onChange({
                        colors: layer.colors.map((item, itemIndex) => itemIndex === index ? { ...item, enabled: e.target.checked } : item),
                      })}
                    />
                    ظاهر
                  </label>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => moveColor(index, -1)} className="px-2 py-1 rounded bg-white border text-sm">↑</button>
                    <button type="button" onClick={() => moveColor(index, 1)} className="px-2 py-1 rounded bg-white border text-sm">↓</button>
                    <button
                      type="button"
                      onClick={() => onChange({ colors: layer.colors.filter((_, itemIndex) => itemIndex !== index) })}
                      className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 text-sm"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DesignPersonalizationFields = ({
  state,
  setState,
  imageUrl,
  mockupImageUrl,
  artworkImageUrl,
  children,
}) => {
  const mockupUrl = mockupImageUrl || imageUrl || "";
  const artworkUrl = artworkImageUrl || "";
  const canvasUrl = state.allowsPersonalization ? artworkUrl || mockupUrl : mockupUrl;
  const usingArtworkFallback = Boolean(state.allowsPersonalization && !artworkUrl && mockupUrl);
  const [fontCatalog, setFontCatalog] = useState(PERSONALIZATION_FONTS);
  const previewRef = useRef(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(PREVIEW_ZOOM_DEFAULT);
  const layers = useMemo(
    () => (state.personalizationLayers?.length ? state.personalizationLayers : defaultLayers()).map(normalizeLayer),
    [state.personalizationLayers]
  );
  const enabledLayers = useMemo(
    () => [...layers.filter((layer) => layer.enabled)].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [layers]
  );
  const stackedFrontToBack = useMemo(
    () => [...enabledLayers].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0)),
    [enabledLayers]
  );
  const [activeKey, setActiveKey] = useState(() => enabledLayers[0]?.key || "ar");
  const [samples, setSamples] = useState({ ar: "محمد", en: "Mohamed" });
  const active = layers.find((layer) => layer.key === activeKey) || enabledLayers[0] || layers[0];
  const topLayerKey = stackedFrontToBack[0]?.key;

  useEffect(() => {
    let cancelled = false;
    fetchPersonalizationFonts()
      .then((fonts) => {
        if (!cancelled && fonts.length) setFontCatalog(fonts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fontCatalog.forEach(loadAdminFont);
    layers.forEach((layer) => {
      const font = fontCatalog.find((item) => item.id === layer.fontId);
      if (font) loadAdminFont(font);
    });
  }, [fontCatalog, layers]);

  useLayoutEffect(() => {
    const node = previewRef.current;
    if (!node) return undefined;
    const updateWidth = () => {
      const next = node.clientWidth;
      if (next > 0) setPreviewWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [canvasUrl, previewZoom]);

  const applyLayers = (nextLayers) => setState({ ...state, ...syncLegacyFromLayers(nextLayers) });
  const updateLayer = (key, patch) => applyLayers(layers.map((layer) => (layer.key === key ? { ...layer, ...patch } : layer)));

  const setTopLayer = (key) => {
    const max = Math.max(...layers.map((layer) => Number(layer.zIndex) || 0), 0);
    applyLayers(layers.map((layer) => ({
      ...layer,
      zIndex: layer.key === key ? max + 1 : Math.max(1, Number(layer.zIndex) || 1),
    })));
  };

  const handlePreviewClick = (event) => {
    if (!active?.enabled) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    updateLayer(active.key, {
      posX: Math.round(x * 10) / 10,
      posY: Math.round(y * 10) / 10,
    });
  };

  const setDesignType = (personalized) => {
    setState({
      ...state,
      allowsPersonalization: personalized,
      ...(personalized ? syncLegacyFromLayers(layers.length ? layers : defaultLayers()) : {}),
    });
  };

  const typeSelector = (
    <div className="space-y-2">
      <p className="font-bold text-gray-800">نوع التصميم</p>
      <p className="text-sm text-gray-600">
        اختار النوع أولًا. التصميم العادي زي الموقع الحالي، والمخصص بالاسم بيستخدم الصورة 2 كآرتورك.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setDesignType(false)}
          className={`text-right rounded-2xl border-2 p-4 transition ${
            !state.allowsPersonalization
              ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200"
              : "border-gray-200 bg-white hover:border-indigo-200"
          }`}
        >
          <span className="block font-bold text-gray-900">تصميم عادي</span>
          <span className="block text-sm text-gray-600 mt-1">موكاب الموقع فقط. من غير إعدادات تخصيص.</span>
        </button>
        <button
          type="button"
          onClick={() => setDesignType(true)}
          className={`text-right rounded-2xl border-2 p-4 transition ${
            state.allowsPersonalization
              ? "border-purple-600 bg-purple-50 ring-2 ring-purple-200"
              : "border-gray-200 bg-white hover:border-purple-200"
          }`}
        >
          <span className="block font-bold text-gray-900">تصميم مخصص بالاسم</span>
          <span className="block text-sm text-gray-600 mt-1">
            الصورة 1 موكاب للموقع، والصورة 2 هي اللي يترسم عليها الاسم.
          </span>
        </button>
      </div>
    </div>
  );

  const fields = state.allowsPersonalization ? (
    <div className="border-2 border-purple-100 rounded-2xl p-4 bg-purple-50/40 space-y-4">
      <p className="font-bold text-purple-800">إعدادات التخصيص</p>
      <p className="text-sm text-gray-600">المعاينة والرسم على صورة التخصيص (الصورة 2)، مش الموكاب.</p>
      <div className="flex flex-wrap gap-2">
        {layers.map((layer) => (
          <button
            key={layer.key}
            type="button"
            onClick={() => {
              if (!layer.enabled) updateLayer(layer.key, { enabled: true });
              setActiveKey(layer.key);
            }}
            className={`px-4 py-2 rounded-xl font-bold border-2 transition ${
              active?.key === layer.key
                ? "bg-purple-700 text-white border-purple-700"
                : layer.enabled
                ? "bg-white text-purple-800 border-purple-200"
                : "bg-gray-100 text-gray-500 border-gray-200"
            }`}
          >
            {layer.label} {layer.enabled ? "" : "(متوقف)"}
          </button>
        ))}
      </div>

      {enabledLayers.length > 1 && (
        <div className="rounded-2xl bg-white border border-purple-200 p-3 space-y-2">
          <p className="font-bold text-purple-800">مين الطبقة اللي فوق؟</p>
          <p className="text-sm text-gray-600">اللي فوق هي اللي تغطي التانية لو الاسمين اتقابلوا.</p>
          <div className="flex flex-wrap gap-2">
            {enabledLayers.map((layer) => (
              <button
                key={layer.key}
                type="button"
                onClick={() => setTopLayer(layer.key)}
                className={`px-4 py-2 rounded-xl border-2 text-sm font-bold ${
                  layer.key === topLayerKey
                    ? "bg-purple-700 text-white border-purple-700"
                    : "bg-white text-purple-800 border-purple-200"
                }`}
              >
                {layer.label} فوق
              </button>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="rounded-2xl bg-white/80 p-4 border border-purple-100">
          <LayerFields layer={active} onChange={(patch) => updateLayer(active.key, patch)} fonts={fontCatalog} />
        </div>
      )}
    </div>
  ) : null;

  const changeZoom = (delta) => {
    setPreviewZoom((current) => Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, current + delta)));
  };

  const preview = (
    <div className="h-full flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-gray-800">المعاينة</p>
          <p className="text-sm text-gray-600">
            {state.allowsPersonalization
              ? usingArtworkFallback
                ? "لا توجد صورة 2 بعد. المعاينة مؤقتًا على الصورة 1 لحد ما ترفع الآرتورك."
                : `المعاينة على صورة التخصيص (الصورة 2). اضغط لتحريك طبقة ${active?.label || ""}.`
              : "دي صورة الموقع (الموكاب). التصميم العادي مش بيرسم اسم فوق الصورة."}
          </p>
        </div>
        {canvasUrl && (
          <div className="flex items-center gap-1 shrink-0 bg-white border border-gray-200 rounded-xl p-1">
            <button
              type="button"
              onClick={() => changeZoom(-PREVIEW_ZOOM_STEP)}
              disabled={previewZoom <= PREVIEW_ZOOM_MIN}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              title="تصغير"
            >
              <ZoomOut size={18} />
            </button>
            <button
              type="button"
              onClick={() => setPreviewZoom(PREVIEW_ZOOM_DEFAULT)}
              className="min-w-[3.5rem] px-1 text-sm font-bold text-gray-700"
              title="إعادة الحجم"
            >
              {previewZoom}%
            </button>
            <button
              type="button"
              onClick={() => changeZoom(PREVIEW_ZOOM_STEP)}
              disabled={previewZoom >= PREVIEW_ZOOM_MAX}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              title="تكبير"
            >
              <ZoomIn size={18} />
            </button>
          </div>
        )}
      </div>
      {state.allowsPersonalization && enabledLayers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {enabledLayers.map((layer) => (
            <label key={layer.key} className="text-sm text-gray-600">
              نص {layer.label}
              <input
                value={samples[layer.key] || ""}
                onChange={(e) => setSamples({ ...samples, [layer.key]: e.target.value })}
                className="w-full mt-1 p-2 border-2 border-gray-200 rounded-xl bg-white"
                dir={layer.direction}
              />
            </label>
          ))}
        </div>
      )}
      {canvasUrl ? (
        <div
          ref={previewRef}
          className={`relative mx-auto cursor-crosshair ${state.allowsPersonalization ? "" : "cursor-default"}`}
          style={{ width: `${previewZoom}%` }}
          onClick={state.allowsPersonalization ? handlePreviewClick : undefined}
        >
          <img
            src={canvasUrl}
            alt="معاينة التصميم"
            className="w-full h-auto rounded-xl border-2 border-purple-200 bg-white object-contain"
            onLoad={() => {
              if (previewRef.current?.clientWidth) setPreviewWidth(previewRef.current.clientWidth);
            }}
          />
          {state.allowsPersonalization && previewWidth > 0 && enabledLayers.map((layer) => {
            const font = fontCatalog.find((item) => item.id === layer.fontId) || fontCatalog[0] || PERSONALIZATION_FONTS[1];
            const family = font.webFamily || font.cssFamily;
            const fontSizePx = ((layer.fontSizePercent || 8) / 100) * previewWidth;
            return (
              <div
                key={layer.key}
                className="personalized-name-overlay absolute pointer-events-none"
                data-font={layer.fontId}
                data-lang={layer.key}
                style={{
                  left: `${layer.posX}%`,
                  top: `${layer.posY}%`,
                  width: `${layer.boxWidthPercent}%`,
                  height: layer.boxHeightPercent > 0 ? `${layer.boxHeightPercent}%` : "auto",
                  transform: `translate(${translateX[layer.alignH] || "-50%"}, ${translateY[layer.alignV] || "-50%"}) rotate(${layer.rotation || 0}deg)`,
                  transformOrigin: "center center",
                  zIndex: layer.zIndex || 1,
                  "--personalization-font-family": `"${family}", sans-serif`,
                  "--personalization-font-size": `${fontSizePx}px`,
                  "--personalization-font-weight": String(font.weight || 400),
                  fontFamily: `"${family}", sans-serif`,
                  fontWeight: font.weight || 400,
                  fontSize: `${fontSizePx}px`,
                  color: layer.textColor || "#fff",
                  textAlign: layer.alignH,
                  direction: layer.direction,
                  letterSpacing: `${layer.letterSpacing || 0}px`,
                  lineHeight: 1.25,
                  overflow: layer.boxHeightPercent > 0 ? "hidden" : "visible",
                  textShadow: "0 1px 2px rgba(0,0,0,0.4)",
                  outline: active?.key === layer.key ? "2px dashed rgba(126,34,206,0.85)" : "none",
                }}
              >
                {samples[layer.key] || LAYER_META[layer.key]?.sample || ""}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 min-h-[280px] rounded-xl border-2 border-dashed border-gray-300 bg-white/70 flex items-center justify-center text-gray-500 text-center px-4">
          {state.allowsPersonalization
            ? "ارفع صورة التخصيص (الصورة 2) عشان المعاينة تترسم على الآرتورك"
            : "ارفع صورة الموقع (الصورة 1) عشان تظهر المعاينة"}
        </div>
      )}
    </div>
  );

  if (children) {
    return (
      <>
        <aside className="min-h-0 overflow-y-auto bg-slate-100 p-4 lg:border-l border-gray-200">
          {preview}
        </aside>
        <div className="min-h-0 overflow-y-auto p-5 space-y-6">
          {typeSelector}
          {children}
          {fields}
        </div>
      </>
    );
  }

  return (
    <div className="md:col-span-2 space-y-4">
      {typeSelector}
      {fields}
      {preview}
    </div>
  );
};

export default DesignPersonalizationFields;
